import { Component, ElementRef, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import {
  delay,
  finalize,
  firstValueFrom,
  map,
  of,
  Subject,
  switchMap,
  take,
  tap,
  timer,
} from 'rxjs';
import { AuthUploadUserService } from 'src/app/_services/auth-upload-user.service';
import { ModalComponent } from 'src/app/shared/components/modal/modal.component';
import { Status, UploadFile } from 'src/app/upload/models/FileOfUpload';
import {
  ModifiedFile,
  Upload,
  UploadDataType,
  UploadFilesType,
} from 'src/app/upload/models/Upload';
import { v4 as uuidv4 } from 'uuid';
import { LoginDTO } from '../../_models/Login';
import { EDataType } from '../../upload/models/DataType';
import { TenantOtpService } from './services/tenant-otp.service';
import { UploadFileOtpService } from './services/upload-file-otp.service';
import { UPLOAD_NAME_PATTERN } from 'src/app/_helpers/constants/regex.constants';
import { UploadOtpService } from './services/upload-otp.service';
import { findFoldersWithMissingChild } from 'src/app/shared/_helpers/upload-tree.helper';

@Component({
  selector: 'app-otp',
  templateUrl: './otp.component.html',
  styleUrls: ['./otp.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OtpComponent {
  readonly Status = Status;
  readonly UploadDataType = UploadDataType;
  @ViewChild('fileInput') fileInput?: ElementRef;
  @ViewChild('folderInput') folderInput?: ElementRef;
  uploadForm: FormGroup;
  isError: boolean = false;
  model: LoginDTO = {
    email: '',
    password: '',
  };
  email = '';
  code = '';
  tenantName = '';
  emailError = false;
  passwordError = false;
  currentFiles: ModifiedFile[] = [];
  MAX_SIZE_OF_FILES_POOL = 1000 * 1073741824; //1 TB
  uploadModel: Upload | undefined = undefined;
  uploadDate: Date;
  xmlFilesUploaded: Upload[] = [];
  alreadyUploadedXml: Upload[] = [];
  isUploading = false;
  isUploadFinished = false;
  history: { uploadedPathId: string; array: any[]; displayName: string }[] = [];
  initialName = '';
  isChekingUnique = false;
  isXmlIncludes = true;
  isImlIncludes = true;
  hasRequiredFile = true;
  isCurrentUpload = false;
  isPartially = false;
  isInitLoading = false;
  notUploadedFiles: string[] = [];
  private notUploadedSet = new Set<string>();
  // Paths that reached storage in this session; notUploadedFiles stays the list of files this upload accepts
  private uploadedPaths = new Set<string>();
  protected foldersWithMissingFiles: ReadonlySet<string> = new Set<string>();
  sizeOfFiles: number = 0;

  rootFolderName = '';
  uploadedFilesOnCheck: string[] = [];
  isLastUploadStopped = false;
  isProjectField = false;
  isContractNameAvailable = false;
  isTypeAvailable = false;

  uploadFileTypeOptions = [
    {
      label: 'Reguläre Untersuchung',
      value: UploadFilesType.GeneralInspection,
    },
    { label: 'Voruntersuchung', value: UploadFilesType.Presurvey },
    { label: 'Nachuntersuchung', value: UploadFilesType.FollowUp },
    {
      label: 'Abnahmebefahrung Baustelle',
      value: UploadFilesType.AcceptanceOfConstruction,
    },
    {
      label: 'Abnahmebefahrung Sanierung',
      value: UploadFilesType.AcceptanceOfRenovation,
    },
    {
      label: 'Gewährleistungsabnahme Baustelle',
      value: UploadFilesType.WarrantyAcceptanceConstructionSite,
    },
    {
      label: 'Gewährleistungsabnahme Sanierung',
      value: UploadFilesType.WarrantyAcceptanceRefurbishment,
    },
  ];
  surveyTypeOptions = [
    { label: 'Bauwerksvermessung', value: UploadFilesType.BuildingSurvey },
    { label: 'Bestandsvermessung', value: UploadFilesType.InventorySurvey },
    { label: 'Schachtvermessung', value: UploadFilesType.ManholeSurvey },
    { label: 'Sonstige Vermessung', value: UploadFilesType.OtherSurvey },
  ];

  uploadDataTypeOptions = [
    {
      label: 'TV-Inspektion',
      value: UploadDataType.TVInspection,
    },
    { label: 'Sonstiges', value: UploadDataType.ConstructionSite },
    { label: 'Vermessung', value: UploadDataType.Survey },
    {
      label: 'Reinigung',
      value: UploadDataType.Cleaning,
    },
    {
      label: 'Hydraulik',
      value: UploadDataType.Hydrodynamic,
    },
  ];

  notUploadedStructure: any[] = [];
  notUploadedStructureFlat: any[] = [];
  prevUploadedFiles: string[] = [];
  isTablet = false;
  onCloseSelectFolder: Subject<string> = new Subject();
  notUploadedFolderPaths: string[] = [];
  isFileDragOver = false;
  isAllowFoldersAndFiles = false;
  isDragingOver = false;
  isDisabledUploading = true;
  currentDataType = null;

  constructor(
    private fb: FormBuilder,
    private authUploadUserService: AuthUploadUserService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private uploadService: UploadOtpService,
    private modalService: BsModalService,
    public uploadFileService: UploadFileOtpService,
    private tenantOtpService: TenantOtpService
  ) {
    this.uploadForm = this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.pattern(UPLOAD_NAME_PATTERN),
        ],
        [this.uniqueUsernameUpdateValidator()],
      ],
      type: ['', Validators.required],
      comment: [''],
      dataType: ['', Validators.required],
      contractName: [
        '',
        [
          Validators.pattern(UPLOAD_NAME_PATTERN),
        ],
      ],
      project: [''],
    });
    this.code = this.route.snapshot.paramMap.get('id') ?? '';
    this.tenantName = localStorage.getItem('otp-name') || '';
    const date = new Date();
    this.uploadDate = new Date(date.toISOString());
    const history: any[] = [];
    this.uploadService
      .filesByCode()
      .pipe(
        tap((res) => {
          this.sizeOfFiles = res.sumSizeOfFile;
          res.uploads
            .sort(
              (a: any, b: any) =>
                new Date(b.uploadDate).getTime() -
                new Date(a.uploadDate).getTime()
            )
            .forEach((item: Upload) => {
              history.push({
                uploadedPathId: item.uploadedPathId,
                array: [item],
                displayName: item.name,
              });
            });
          this.history = history;
        }),
        switchMap(() => this.uploadFileService.uploadedFiles$)
      )
      .subscribe((uploadedFiles: string[]) => {
        this.checkUploadFileStructure(uploadedFiles);
      });
    this.isTablet = this.checkIsTablet();
    this.uploadForm
      .get('name')!
      .valueChanges.subscribe(() => this.changeTopFolderName());
  }

  // Helper method to get upload status with backward compatibility
  getUploadStatus(upload: Upload): Status | undefined {
    // Use new upload.status if available, otherwise fall back to files[0].status
    return upload.status ?? upload.files?.[0]?.status;
  }

  private getMissingValues(uploadfiles: string[], filesToUpload: string[]) {
    const uploadedSet = new Set(uploadfiles);
    for (const file of filesToUpload) {
      if (!uploadedSet.has(file)) {
        this.notUploadedFiles.push(file);
      }
    }
    this.notUploadedSet = new Set(this.notUploadedFiles);
  }

  get progressPercent(): number {
    const uploaded = this.uploadFileService.filesAlreadyUpload$.value ?? 0;
    const total = this.uploadFileService.filesShouldUpload$.value ?? 0;
    return total > 0 ? (uploaded / total) * 100 : 0;
  }

  checkEmail() {
    const body = {
      email: this.uploadForm.get('email')!.value,
      parameter: this.code,
    };
    this.authUploadUserService.checkCode(body).subscribe({
      next: () => (this.email = body.email),
      error: () => (this.emailError = true),
    });
  }

  checkPassword() {
    const body = {
      email: this.uploadForm.get('email')!.value,
      parameter: this.uploadForm.get('password')!.value,
    };
    this.authUploadUserService.loginByCode(body).subscribe({
      next: () => console.log('success'),
      error: () => (this.passwordError = true),
    });
  }

  /**
   * Continues a Partially upload from the history list. The list item already has the metadata and the uploaded
   * files; only filesTryToUpload is left out of the list response, so that is the one thing fetched.
   */
  reupload(event: MouseEvent, upload: Upload): void {
    event.stopPropagation();
    if (
      this.getUploadStatus(upload) !== Status.Partially &&
      upload.files?.find((file) => file.extension?.toLowerCase() === 'xml')?.status !== Status.Partially &&
      upload.files?.find((file) => file.extension?.toLowerCase() === 'iml')?.status !== Status.Partially
    ) {
      return;
    }

    this.uploadService.getUploadById(upload.id!).subscribe((res) => {
      // Copy without the history tree state (isOpen, filesStructureFlat), which holds circular parent references
      // and must not be sent to the server with the upload
      const { isOpen: _isOpen, filesStructureFlat: _filesStructureFlat, ...listUpload } = upload as any;
      const uploadModel: Upload = { ...listUpload, filesTryToUpload: res?.filesTryToUpload ?? [] };

      this.uploadForm = this.fb.group({
        name: [
          uploadModel.name,
          [Validators.required],
          [this.uniqueUsernameUpdateValidator()],
        ],
        type: [uploadModel.type, Validators.required],
        comment: [uploadModel.comment],
        project: [uploadModel.project],
        contractName: [uploadModel.contractName],
        dataType: [uploadModel.dataType, Validators.required],
      });

      this.isCurrentUpload = true;
      this.isPartially = true;
      this.isInitLoading = false;
      this.uploadModel = uploadModel;
      this.xmlFilesUploaded = [uploadModel];

      this.prevUploadedFiles = (uploadModel.files ?? []).map((file) => file.filePath || file.name);
      this.getMissingValues(this.prevUploadedFiles, uploadModel.filesTryToUpload as string[]);
      this.notUploadedStructure = this.divideFolderStructure(this.notUploadedFiles);
      this.notUploadedStructureFlat = this.flattenTree(this.notUploadedStructure);
      this.refreshFoldersWithMissingFiles();
    });
  }

  cancelAndDeleteUpload(): void {
    const initialState = {
      title: 'Aktuellen Upload abbrechen?',
      description: `Möchten Sie den aktuellen Upload wirklich abbrechen? Alle bereits hochgeladenen Daten gehen verloren.`,
      icon: 'icon-delete',
      denyButton: 'Nein',
      acceptButton: 'Ja',
    };
    const modalRef = this.modalService.show(ModalComponent, {
      class: 'modal-md modal-dialog-centered',
      initialState,
    });
    modalRef.content
      ?.closeModalEvent!.pipe(take(1))
      .subscribe((isApproved: boolean) => {
        if (isApproved && this.uploadFileService.isFilesUploading$.value) {
          this.uploadFileService.isDeleting$.next(true);
          this.uploadFileService.stopUploading$.next(true);
          this.uploadFileService.abortController?.abort();
          this.toastr.success(
            'Der Upload wurde abgebrochen.',
            'Der Upload abgebrochen.'
          );
        }
        modalRef?.hide();
      });
  }

  cancelUpload(): void {
    if (this.uploadFileService.isFilesUploading$.value) {
      const initialState = {
        title: 'Upload pausieren?',
        description: `Möchten Sie den aktuellen Upload pausieren? Sie können den Upload später fortsetzen.`,
        icon: '',
        denyButton: 'Nein',
        acceptButton: 'Ja',
      };
      const modalRef = this.modalService.show(ModalComponent, {
        class: 'modal-md modal-dialog-centered',
        initialState,
      });
      modalRef.content
        ?.closeModalEvent!.pipe(take(1))
        .subscribe((isApproved: boolean) => {
          if (isApproved && this.uploadFileService.isFilesUploading$.value) {
            this.uploadFileService.stopUploading$.next(true);
            this.uploadFileService.abortController?.abort();
            this.toastr.success(
              'Der Upload wurde unterbrochen und kann zu einem späteren Zeitpunkt fortgesetzt werden.',
              'Der Upload unterbrochen.'
            );
          }
          modalRef?.hide();
        });
    } else {
      window.location.reload();
    }
  }

  async upload() {
    const isContinueUpload = !!this.uploadModel?.uploadedPathId;
    if (isContinueUpload) {
      this.history.find(
        (item) => item.uploadedPathId === this.uploadModel!.uploadedPathId
      );
      this.uploadFileService.uploadModelIds =
        this.history
          .find(
            (group) => group.uploadedPathId === this.uploadModel!.uploadedPathId
          )
          ?.array.map((item) => item.id as string) || [];
    }
    this.uploadFileService.xmlFileUpload$.subscribe((data) =>
      this.xmlFilesUploaded.push(data)
    );
    //Check if upload has one .xml file
    let xmlFiles: any[] = [];
    if (this.hasRequiredFile) {
      xmlFiles = Array.from(this.currentFiles).filter(
        (x) =>
          x.name.split('.').pop()?.toLowerCase() === 'xml' ||
          x.name.split('.').pop()?.toLowerCase() === 'iml'
      );
      if (xmlFiles.length <= 0 && this.uploadModel === undefined) {
        this.toastr.error(
          'Es muss mindestens eine Datei mit der Endung .xml or .iml hochgeladen werden!',
          'Fehler'
        );
        return;
      }
    } else {
      xmlFiles = Array.from(this.currentFiles).slice(0, 1);
    }
    this.isUploading = true;
    this.uploadForm.disable();
    this.uploadFileService.isPartialUploadLoading = this.isPartially;
    //Upload to azure proccess
    // Fresh SAS token for this upload; the queue renews it while the upload runs
    const containerClient = this.uploadFileService.getUploadContainerClient(true);
    if (!(await containerClient.exists())) {
      console.log('the container does not exit');
      await containerClient.create();
    }
    let arrayAdditionalFiles: any[] = [];
    if (this.hasRequiredFile) {
      arrayAdditionalFiles = Array.from(
        this.currentFiles.filter(
          (x) =>
            x.name.toLowerCase().split('.').pop() !== 'xml' &&
            x.name.toLowerCase().split('.').pop() !== 'iml'
        )
      );
    } else {
      arrayAdditionalFiles = Array.from(this.currentFiles).slice(
        1,
        this.currentFiles.length
      );
    } //Create unic filePath
    let uploadFilePath = this.uploadModel?.uploadedPathId;
    if (this.uploadModel === undefined) {
      uploadFilePath = uuidv4();
    }
    let oldFiles: any[] | undefined = [];
    if (this.hasRequiredFile) {
      oldFiles = this.uploadModel?.files?.filter(
        (f) =>
          f.extension!.toLowerCase() !== 'xml' &&
          f.extension!.toLowerCase() !== 'iml'
      );
    } else {
      oldFiles = this.uploadModel?.files;
    }
    if (this.isPartially && oldFiles && oldFiles.length > 0) {
      oldFiles.forEach((file) => (file.status = Status.Uploading));
      await this.uploadService
        .changeStatusFiles(oldFiles)
        .pipe(take(1))
        .toPromise();
    }
    //Save xml files
    if (this.currentFiles.length > 0) {
      const totalSize = this.currentFiles.reduce(
        (partialSum, a) => partialSum + a.size,
        0
      );
      this.uploadFileService.filesShouldUpload$.next(totalSize);
      const updatedXmlFile: UploadFile[] = [];
      let uploadedFiles: number;
      if (!isContinueUpload) {
        const uploadModel = await this.uploadFileService.uploadMainFiles(
          xmlFiles,
          containerClient,
          uploadFilePath!,
          arrayAdditionalFiles,
          this.uploadForm,
          this.uploadDate,
          this.alreadyUploadedXml,
          oldFiles,
          this.uploadModel
        );
        this.uploadModel = uploadModel;
        this.uploadFileService.xmlFilesUploaded = this.xmlFilesUploaded;
        console.log('Finish Upload xml');
        //Change status of upload before upload new files
        if (this.uploadModel && arrayAdditionalFiles.length > 0) {
          this.uploadModel!.status = Status.Uploading;
          updatedXmlFile.concat(this.uploadModel!.files!);
          this.uploadService.createUpdateUpload(this.uploadModel!).subscribe();
        }
        uploadedFiles = xmlFiles.length;
        if (arrayAdditionalFiles.length === 0) {
          this.uploadModel.status = Status.Open;
        }
      } else {
        if (this.uploadModel && arrayAdditionalFiles.length > 0) {
          this.uploadModel.status = Status.Uploading;
          this.uploadFileService.xmlFilesUploaded.push(this.uploadModel!);
          this.uploadService.createUpdateUpload(this.uploadModel!).subscribe();
        }
        arrayAdditionalFiles = arrayAdditionalFiles.filter(
          (file: any) =>
            !this.uploadModel?.files?.some(
              (uploadedFile) =>
                (uploadedFile.filePath === file.modifiedPath ||
                  uploadedFile.filePath === file.webkitRelativePath) &&
                uploadedFile.name === file.name
            )
        );
        const size = this.isPartially
          ? 0
          : this.uploadModel?.files?.reduce(
              (partialSum, a) => partialSum + a.sizeOfFile,
              0
            );
        this.uploadFileService.filesAlreadyUpload$.next(size || 0);
        uploadedFiles = this.uploadModel!.files!.length;
      }
      let notUploadedCount = 0;
      //Save additional files
      if (arrayAdditionalFiles.length > 0) {
        notUploadedCount = await this.uploadFileService.uploadAdditionalFiles(
          arrayAdditionalFiles,
          uploadFilePath,
          uploadedFiles,
          this.currentFiles,
          this.uploadModel!.id!
        );
        console.log('Finish Upload add file');
        console.log('Status check:', {
          filesTryToUpload: this.uploadModel?.filesTryToUpload?.length || 0,
          currentFiles: this.currentFiles.length,
          stopUploading: this.uploadFileService.stopUploading$.value,
          filesTryToUploadPaths: this.uploadModel?.filesTryToUpload,
          currentFilesPaths: this.currentFiles.map(
            (f) => f.modifiedPath || f.webkitRelativePath || f.name
          ),
        });
        if (isContinueUpload) {
          const alreadyUploadedAdditionalFiles = Array.from(
            this.uploadModel!.files!
          ).filter(
            (x) =>
              x.name.toLowerCase().split('.').pop() !== 'xml' &&
              x.name.toLowerCase().split('.').pop() !== 'iml'
          );
          this.uploadModel!.status =
            this.uploadFileService.stopUploading$.value ||
            notUploadedCount > 0 ||
            this.uploadModel!.filesTryToUpload?.length !==
              this.currentFiles.length + alreadyUploadedAdditionalFiles.length
              ? Status.Partially
              : Status.Open;
        } else {
          // Selected and planned files always match here, so only the actual upload result decides
          this.uploadModel!.status =
            this.uploadFileService.stopUploading$.value || notUploadedCount > 0
              ? Status.Partially
              : Status.Open;
        }
      }
      if (this.uploadFileService.stopUploading$.value) {
        this.toastr.success(
          'Sie haben das Hochladen von Dateien auf den Server gestoppt',
          'Der Upload wurde gestoppt'
        );
      } else if (notUploadedCount > 0) {
        this.toastr.warning(
          `${notUploadedCount} Dateien konnten nicht hochgeladen werden. Bitte laden Sie die fehlenden Dateien erneut hoch.`,
          'Upload unvollständig',
          { timeOut: 0, extendedTimeOut: 0, closeButton: true }
        );
      } else {
        this.toastr.success(
          'Sie haben erfolgreich Dateien auf den Server hochgeladen',
          'Erfolgreich hinzugefügt'
        );
      }
    }
    //If upload has changes and was not cancelled (a cancelled upload is already deleted on the server)
    if (!this.uploadFileService.isDeleting$.value && this.uploadModel!.files !== null && this.uploadModel!.files!.length > 0 && this.uploadModel!.files?.[0].id !== undefined) {
      this.uploadModel!.name = this.uploadForm.controls['name'].value;
      if (!this.uploadModel?.name?.includes('-External')) {
        this.uploadModel!.name = this.uploadModel?.name + '-External';
      }
      this.uploadModel!.comment = this.uploadForm.controls['comment'].value;
      this.uploadModel!.contractName =
        this.uploadForm.controls['contractName'].value;
      this.uploadModel!.type =
        this.uploadForm.controls['type'].value !== null
          ? Number(this.uploadForm.controls['type'].value)
          : null;
      this.uploadModel!.dataType = Number(
        this.uploadForm.controls['dataType'].value
      );
      if (
        +this.uploadForm.get('dataType')! === +EDataType.Construction ||
        +this.uploadForm.get('dataType')! === +EDataType.Hydrodynamic
      ) {
        this.uploadModel!.project = this.uploadForm.controls['project'].value;
      }
    }
    await firstValueFrom(
      this.uploadService.createUpdateUpload(this.uploadModel!)
    );
    this.isUploading = false;
    this.currentFiles = [];
    this.uploadForm.markAsUntouched();
    this.isLastUploadStopped = this.uploadFileService.stopUploading$.value;
    this.uploadFileService.stopUploading$.next(false);
    this.uploadFileService.isPartialUploadLoading = false;
    if (this.uploadFileService.isDeleted$.value) {
      this.newUpload();
    } else {
      this.isUploadFinished = true;
    }
  }

  onFileReChangeAllAllow(
    isFileSelected: boolean,
    event: any,
    folderPath?: string
  ): void {
    this.isFileDragOver = isFileSelected;
    this.onFileReChange(event, folderPath);
  }

  onFileChange(event: any, folderPath?: string): void {
    const files = event.target.files;
    // Empty FileList happens both when the user cancels the browser's
    // "Upload to this site?" folder confirmation and when they pick an
    // empty folder - we can't tell these apart, so just inform the user.
    if (!files || files.length === 0) {
      event.target.value = '';
      this.toastr.info(
        'Es wurden keine Dateien ausgewählt.',
        'Keine Dateien ausgewählt'
      );
      return;
    }
    this.onFileReChange(Array.from(files), folderPath);
  }

  onFileReChange(files: any, folderPath?: string): void {
    const isTVInspection =
      +this.uploadForm.get('dataType')!.value === +EDataType.Inspection;
    const { validFiles, disallowedFiles } = this.filterDisallowedFiles(
      files,
      isTVInspection
    );

    if (disallowedFiles.length > 0) {
      this.toastr.error(
        `Die folgenden Dateien sind für TV-Inspektionen nicht erlaubt: ${disallowedFiles.join(', ')}. Bitte wählen Sie andere Dateiformate.`,
        'Unzulässiges Dateiformat'
      );
      if (validFiles.length === 0) {
        return;
      }
    }

    let newFiles = validFiles.map((item: any) => {
      if (folderPath !== undefined) {
        item.modifiedPath = `${folderPath || 'Hauptordner/'}${item.name}`;
      }
      const file = item;
      if (this.notUploadedStructure.length) {
        this.checkAddedFilesStructure(
          file.modifiedPath || file.webkitRelativePath,
          this.notUploadedStructure
        );
        this.checkAddedFolderStructure(this.notUploadedStructure);
      }
      return file;
    });
    this.syncFlatStructure();
    newFiles = newFiles.filter((item: any) => {
      return this.notUploadedFiles.length
        ? this.notUploadedSet.has(
            item.modifiedPath ? item.modifiedPath : item.webkitRelativePath
          )
        : item;
    }) as ModifiedFile[];
    const oldFiles: ModifiedFile[] = this.currentFiles.filter(
      (file) =>
        !newFiles.some((nf) =>
          !this.isTablet && !this.isFileDragOver
            ? nf.webkitRelativePath !== file.webkitRelativePath
            : nf.modifiedPath === file.modifiedPath
        )
    );
    this.currentFiles = oldFiles.concat(newFiles);
    const sum = this.currentFiles.reduce(
      (partialSum, a) => partialSum + a.size,
      0
    );
    if (sum + this.sizeOfFiles > this.MAX_SIZE_OF_FILES_POOL) {
      this.currentFiles = [];
      this.toastr.error(
        'Sie haben Ihr Upload-Limit für Dateien überschritten. Bitte überprüfen Sie Ihr verfügbares Limit und versuchen Sie es erneut',
        'Fehler'
      );
      return;
    }
    this.hasRequiredFile =
      +this.uploadForm.get('dataType')?.value === +EDataType.Inspection ||
      +this.uploadForm.get('dataType')?.value === +EDataType.Cleaning;
    this.currentFiles = this.currentFiles.filter(
      (x) => x.name.split('.').pop() !== 'DS_Store'
    );
    if (!newFiles.length) {
      this.toastr.error(
        'Bitte lesen Sie die Upload-Anweisungen und versuchen Sie es noch einmal',
        'Sie haben falsche Dateien hochgeladen'
      );
    }
    if (!this.isPartially) {
      this.reBuildStructure();
      if (
        this.isXmlIncludes ||
        (this.uploadForm.get('dataType')?.value !== UploadDataType.Cleaning &&
          this.uploadForm.get('dataType')?.value !==
            UploadDataType.TVInspection)
      ) {
        this.toastr.success(
          'Du kannst die gewählten Dateien jetzt hochladen!',
          'Erfolgreich hinzugefügt'
        );
      } else if (!this.isXmlIncludes) {
        this.toastr.error(
          'Sie können die ausgewählten Dateien nicht hochladen!',
          'Keine XML-Datei enthalten'
        );
      } else {
        this.toastr.error(
          'Sie können die ausgewählten Dateien nicht hochladen!',
          'Keine Iml-Datei enthalten'
        );
      }
    }
  }

  reBuildStructure() {
    if (!this.isPartially) {
      this.notUploadedStructure = this.divideFolderStructure(
        this.currentFiles.map(
          (file: ModifiedFile) => file.modifiedPath || file.webkitRelativePath
        )
      );
      this.currentFiles.forEach((file: ModifiedFile) => {
        this.checkAddedFilesStructure(
          file.modifiedPath || file.webkitRelativePath,
          this.notUploadedStructure
        );
        this.checkAddedFolderStructure(this.notUploadedStructure);
      });
      this.changeTopFolderName();
      this.notUploadedStructureFlat = this.flattenTree(
        this.notUploadedStructure
      );
      this.refreshFoldersWithMissingFiles();
      this.isXmlIncludes = this.currentFiles.some(
        (x) => x.name.split('.').pop()?.toLowerCase() === 'xml'
      );
      this.isImlIncludes = this.currentFiles.some(
        (x) => x.name.split('.').pop()?.toLowerCase() === 'iml'
      );
    }
  }

  divideFolderStructure(list: string[]) {
    const ar: any[] = [
      {
        name: 'Hauptordner',
        type: 'folder',
        isOpen: true,
        path: 'Hauptordner/',
        children: [],
      },
    ];
    this.notUploadedFolderPaths = [];
    const validList = (list || []).filter(
      (item): item is string => !!item && typeof item === 'string'
    );
    validList.forEach((item) => this.divideFolder(item, item, ar[0].children));
    const firstFileFolder =
      !!validList.length &&
      validList[0].indexOf('/') !== -1 &&
      validList[0].slice(0, validList[0].indexOf('/'));
    const isFolderOnTopLayer =
      !!validList.length &&
      !!firstFileFolder &&
      validList.every((item) => {
        const index = item.indexOf('/');
        if (index === -1) {
          return false;
        }
        return item.slice(0, index) === firstFileFolder;
      });
    return isFolderOnTopLayer ? ar[0].children : ar;
  }

  private divideFolder(name: string, path: string, ar: any[]) {
    if (!name || typeof name !== 'string') return;
    const index = name.indexOf('/');
    if (index === -1) {
      ar.push({ name, path, type: 'file', isAdded: false, isUploaded: false });
      return;
    } else {
      const nameFolder = name.slice(0, index);
      let folder = ar.find((item) => item.name === nameFolder);
      if (!folder) {
        ar.push({
          name: nameFolder,
          type: 'folder',
          isOpen: true,
          path: path.slice(0, -name.slice(index + 1).length),
          isAdded: false,
          children: [],
        });
        folder = ar[ar.length - 1];
        this.notUploadedFolderPaths.push(folder.path);
      }
      this.divideFolder(name.slice(index + 1), path, folder.children);
    }
  }

  checkAddedFilesStructure(
    path: string,
    structure: any[],
    isRemove = false
  ): boolean {
    return structure.some((item) => {
      if (item.modifiedPath === path || item.path === path) {
        item.isAdded = !isRemove;
        return true;
      }
      if (item.type === 'folder') {
        return this.checkAddedFilesStructure(path, item.children, isRemove);
      }
      return false;
    });
  }

  checkAddedFolderStructure(structure: any[]): boolean {
    return structure.every((item) => {
      if (item.type === 'folder') {
        const foldersChildren = item.children.filter(
          (child: any) => child.type === 'folder'
        );
        this.checkAddedFolderStructure(foldersChildren);
        item.isAdded = item.children.every((child: any) => child.isAdded);
      }
      return item.isAdded;
    });
  }

  checkUploadFileStructure(list: string[]): void {
    if (list.length) {
      const checked = new Set(this.uploadedFilesOnCheck);
      const newList = list.filter((item) => !checked.has(item));
      if (newList.length) {
        this.updateUploadFilesStructure(newList);
      }
    }
  }

  updateUploadFilesStructure(list: string[]): void {
    for (const path of list) {
      this.uploadedPaths.add(path);
    }
    this.uploadFileService.markUploaded(this.notUploadedStructure, list);
    this.uploadedFilesOnCheck = list;
    this.syncFlatStructure();
    this.refreshFoldersWithMissingFiles();
  }

  /** The flat list holds copies of the tree nodes, so flags changed on the tree must be copied over */
  private syncFlatStructure(): void {
    const treeNodes = new Map<string, any>();
    const collect = (nodes: any[]): void =>
      nodes.forEach((node) => {
        treeNodes.set(node.path, node);
        if (node.type === 'folder') {
          collect(node.children);
        }
      });
    collect(this.notUploadedStructure);

    this.notUploadedStructureFlat.forEach((flatNode) => {
      const treeNode = treeNodes.get(flatNode.path);
      if (treeNode) {
        flatNode.isAdded = treeNode.isAdded;
        flatNode.isUploaded = treeNode.isUploaded;
      }
    });
  }

  // True while the file is still missing: planned for this upload and not uploaded in this session
  isUploadedFiles(value: string): boolean {
    return this.notUploadedSet.has(value) && !this.uploadedPaths.has(value);
  }

  private refreshFoldersWithMissingFiles(): void {
    this.foldersWithMissingFiles = findFoldersWithMissingChild(this.notUploadedStructureFlat, (path) =>
      this.isUploadedFiles(path)
    );
  }

  newUpload(): void {
    this.uploadFileService.isFilesUploading$.next(false);
    window.location.reload();
  }

  uniqueUsernameUpdateValidator(): AsyncValidatorFn {
    return (c: AbstractControl): { [key: string]: boolean } | any => {
      if (!c.value || c.value === this.initialName) {
        this.isChekingUnique = false;
        return of(null);
      }
      this.isChekingUnique = true;
      // Angular cancels this observable on the next value change, so only the name typed last is checked
      return timer(500).pipe(
        switchMap(() => this.uploadService.checkUploadName(c.value)),
        map((isExist) => {
          return isExist ? { nameExist: true } : null;
        }),
        // Also runs when a newer value cancels this check or the request fails, so the flag never stays set
        finalize(() => (this.isChekingUnique = false))
      );
    };
  }

  changeUploadStatusToUploaded(): void {
    this.uploadModel!.status = Status.Open;
    this.uploadService
      .createUpdateUpload(this.uploadModel!)
      .pipe(take(1))
      .subscribe(() => this.newUpload());
  }

  logout() {
    if (this.uploadFileService.isFilesUploading$.value) {
      const initialState = {
        title: 'Upload pausieren und Ausloggen?',
        description: `Möchten Sie den aktuellen Upload pausieren? Sie können den Upload später fortsetzen.`,
        icon: 'icon-delete',
        denyButton: 'Nein',
        acceptButton: 'Ja',
      };
      const modalRef = this.modalService.show(ModalComponent, {
        class: 'modal-md modal-dialog-centered',
        initialState,
      });
      modalRef.content
        ?.closeModalEvent!.pipe(
          take(1),
          tap((isApproved: boolean) => {
            if (isApproved && this.uploadFileService.isFilesUploading$.value) {
              this.uploadFileService.stopUploading$.next(true);
              this.uploadFileService.abortController?.abort();
              this.toastr.success(
                'Der Upload wurde unterbrochen und kann zu einem späteren Zeitpunkt fortgesetzt werden.',
                'Der Upload unterbrochen.'
              );
            }
            modalRef?.hide();
          }),
          delay(500)
        )
        .subscribe((isApproved: boolean) => {
          if (isApproved) {
            this.clearDataAndLogout();
          }
        });
    } else {
      this.clearDataAndLogout();
    }
  }

  clearDataAndLogout() {
    this.tenantOtpService.clearOtpData();
    this.router.navigateByUrl(`otp/logout`);
  }

  openHistoryItem(item: any) {
    // Build tree structure if not already built
    if (!item.filesStructureFlat) {
      this.buildHistoryFileTree(item);
    }
    item.isOpen = !item.isOpen;
  }

  buildHistoryFileTree(item: any) {
    if (item.files && Array.isArray(item.files) && item.files.length > 0) {
      // Build tree structure from file paths
      const filePaths = (item.files as any[]).map(
        (file) => file.filePath || file.name
      );
      const treeStructure = this.divideFolderStructure(filePaths);

      // Mark files as added in the tree structure
      (item.files as any[]).forEach((file) => {
        this.checkAddedFilesStructure(
          file.filePath || file.name,
          treeStructure
        );
      });
      this.checkAddedFolderStructure(treeStructure);

      // Flatten the tree for virtual scrolling
      item.filesStructureFlat = this.flattenTree(treeStructure);
    }
  }

  updateHistoryItemFiles(item: any) {
    this.uploadService.getUploadById(item.id).subscribe((res) => {
      if (res && res.files) {
        item.files = res.files;

        if (
          res.files.find((file) => file.extension?.toLowerCase() === 'xml')
            ?.status === Status.Open ||
          res.files.find((file) => file.extension?.toLowerCase() === 'iml')
            ?.status === Status.Open
        ) {
          const files = (item.files as any[])
            .filter(
              (file) =>
                file.extension?.toLowerCase() !== 'xml' &&
                file.extension?.toLowerCase() !== 'iml'
            )
            .map((file) => file.filePath || file.name);
          item.structure = this.divideFolderStructure(files);
        }

        (item.files as any[]).forEach((file) => {
          this.checkAddedFilesStructure(file.filePath, item.structure);
          this.checkAddedFolderStructure(item.structure);
        });
      }
      item.isOpen = !item.isOpen;
    });
  }

  private checkIsTablet(): boolean {
    // navigator.userAgentData.mobile is the most reliable modern signal
    if (
      'userAgentData' in navigator &&
      (navigator as any).userAgentData?.mobile !== undefined
    ) {
      return (navigator as any).userAgentData.mobile;
    }
    // Fallback: check user agent string for mobile/tablet keywords
    const ua = navigator.userAgent;
    const isMobileUA = /android|ipad|iphone|ipod|tablet|mobile|touch/i.test(ua);
    if (isMobileUA) {
      return true;
    }
    // Last resort: touch points alone are not enough (Windows desktops report maxTouchPoints > 0),
    // so require touch AND a coarse pointer (no fine mouse pointer available)
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const hasCoarsePointerOnly = window.matchMedia(
      '(pointer: coarse) and (hover: none)'
    ).matches;
    return hasTouch && hasCoarsePointerOnly;
  }

  changeTopFolderName() {
    if (
      (this.isTablet || this.isFileDragOver) &&
      this.uploadForm.get('name')?.value &&
      this.notUploadedStructure.length
    ) {
      this.notUploadedStructure[0] = {
        ...this.notUploadedStructure[0],
        name: this.uploadForm.get('name')!.value,
        path: `${this.uploadForm.get('name')!.value}/`,
        children: this.notUploadedStructure[0].children.map((ch: any) => {
          return {
            ...ch,
            path: `${this.uploadForm.get('name')!.value}/${ch.name}`,
          };
        }),
      };
      this.currentFiles = this.currentFiles.map((item) => {
        item.modifiedPath = `${this.uploadForm.get('name')!.value}/${item.name}`;
        return item;
      });
    }
  }

  chooseDataType(value: any) {
    this.isDisabledUploading = false;
    if (this.currentFiles.length > 0) {
      const initialState = {
        title:
          'Wenn Sie den Upload-Typ ändern, werden alle bereits hinzugefügten Dateien gelöscht. Möchten Sie fortfahren?',
        icon: 'icon-info',
        denyButton: 'Nein',
        acceptButton: 'Ja',
      };
      const modalRef = this.modalService.show(ModalComponent, {
        class: 'modal-md modal-dialog-centered',
        initialState,
      });
      modalRef.content
        ?.closeModalEvent!.pipe(take(1))
        .subscribe((isApproved: boolean) => {
          if (isApproved) {
            this.currentFiles = [];
            this.notUploadedStructure = [];
            this.notUploadedStructureFlat = [];
            this.refreshFoldersWithMissingFiles();
            this.currentDataType = value;
            this.reChangeUploadForm(value);
          } else {
            this.uploadForm.get('dataType')?.setValue(this.currentDataType);
          }
          modalRef?.hide();
        });
    } else {
      this.currentDataType = value;
      this.reChangeUploadForm(value);
    }
  }

  submitSelectFolder(path: string): void {
    this.onCloseSelectFolder.next(path);
  }

  selectFolderLocatedFiles(event: any, template: TemplateRef<any>) {
    const modalSelectFolder = this.modalService.show(template, {
      class: 'modal-md modal-dialog-centered',
    });
    const req = this.onCloseSelectFolder.subscribe((path: string) => {
      if (path) {
        this.onFileChange(event, path);
        req.unsubscribe();
        modalSelectFolder.hide();
      } else {
        req.unsubscribe();
        modalSelectFolder.hide();
      }
    });
  }

  removeFileStructure(item: any): void {
    this.currentFiles = this.currentFiles.filter(
      (f) =>
        !(f.modifiedPath === item.path || f.webkitRelativePath === item.path)
    );
    if (this.isCurrentUpload) {
      this.checkAddedFilesStructure(item.path, this.notUploadedStructure, true);
      this.checkAddedFolderStructure(this.notUploadedStructure);
      this.syncFlatStructure();
    } else {
      this.reBuildStructure();
    }
  }

  detectFilesOrFolder(event: DragEvent) {
    event.preventDefault();
    this.isDragingOver = true;
    this.isFileDragOver = !!event.dataTransfer?.items[0].type;
  }

  stopDragOver(): void {
    this.isDragingOver = false;
  }
  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragingOver = false;
    const files = event.dataTransfer?.files;
    if (files) {
      this.onFileReChange(
        files,
        this.uploadForm.get('name')?.value
          ? this.uploadForm.get('name')?.value + '/'
          : this.uploadForm.get('name')?.value
      );
    }
  }

  changeFileFolderAccept(_isFileAccept: boolean): void {
    this.isFileDragOver = false;
  }

  onFileChangeAllAllow(event: any, isFileSelected: boolean) {
    this.isFileDragOver = isFileSelected;
    this.onFileChange(event, '');
  }

  onFileChangeAllAllowDragDrop(event: any) {
    this.stopDragOver();
    const files = event.target.files;
    if (!files || files.length === 0) {
      event.target.value = '';
      this.toastr.info(
        'Es wurden keine Dateien ausgewählt.',
        'Keine Dateien ausgewählt'
      );
      return;
    }
    if (this.currentFiles.length && this.isFileDragOver && !files[0].type) {
      return;
    }
    this.onFileChange(event);
  }

  flattenTree(data: any[], level = 0, parent?: any): any[] {
    let result: any[] = [];
    for (const item of data) {
      const node: any = {
        isAdded: item.isAdded,
        isUploaded: item.isUploaded,
        isOpen: item.isOpen,
        name: item.name,
        path: item.path,
        type: item.type,
        level,
        parent,
        childenPath:
          Array.isArray(item.children) &&
          item.children.map((ch: any) => ch.path),
      };

      result.push(node);

      if (node.childenPath.length) {
        result = result.concat(
          this.flattenTree(item.children, level + 1, node)
        );
      }
    }

    return result;
  }

  openCloseItem(item: any) {
    item.isOpen = !item.isOpen;
  }

  private filterDisallowedFiles(
    files: any[],
    isTVInspection: boolean
  ): { validFiles: any[]; disallowedFiles: string[] } {
    if (!isTVInspection) {
      return { validFiles: files, disallowedFiles: [] };
    }
    const disallowedExtensions = ['zip', 'rar'];
    const disallowedFiles: string[] = [];
    const validFiles = files.filter((item: any) => {
      const fileExtension = item.name.toLowerCase().split('.').pop();
      const isDisallowed = disallowedExtensions.includes(fileExtension);
      if (isDisallowed) {
        disallowedFiles.push(item.name);
      }
      return !isDisallowed;
    });

    return { validFiles, disallowedFiles };
  }

  private reChangeUploadForm(value: number) {
    this.isTypeAvailable =
      value === UploadDataType.TVInspection || value === UploadDataType.Survey;
    if (!this.isTypeAvailable) {
      this.uploadForm.get('type')?.clearValidators();
      this.uploadForm.get('type')?.setValue(null);
    } else {
      this.uploadForm.get('type')?.setValidators(Validators.required);
    }
    if (
      value === UploadDataType.TVInspection ||
      value === UploadDataType.Survey ||
      value === UploadDataType.Cleaning
    ) {
      this.isContractNameAvailable = true;
    } else {
      this.uploadForm.get('contractName')?.setValue('');
      this.isContractNameAvailable = false;
    }
    this.isAllowFoldersAndFiles =
      !this.isTablet &&
      (value === UploadDataType.ConstructionSite ||
        value === UploadDataType.Survey ||
        value === UploadDataType.Hydrodynamic);
    this.isProjectField =
      value === UploadDataType.Hydrodynamic ||
      value === UploadDataType.ConstructionSite;
  }
}
